import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime } from 'rxjs';
import { PButtonComponent } from '../../../../../shared/design-system/atoms/p-button/p-button.component';
import { PInputComponent } from '../../../../../shared/design-system/atoms/p-input/p-input.component';
import { PSelectComponent } from '../../../../../shared/design-system/atoms/p-select/p-select.component';
import { IconsModule } from '../../../../../shared/design-system/icons/icons.module';
import { PervalValidators } from '../../../../../shared/validators/perval-validators';
import { SignUpInformationsData } from '../../interfaces/sign-up-store.interface';
import { pervalLoginValidator } from '../../../../../../../../../libs/admin/src/lib/validators/perval-login.validator';
import { getFirstControlErrorMessage } from '../../../../../shared/utils/forms.utils';
import { MES_INFORMATIONS_ERROR_MESSAGES } from '../../../../../shared/errors/forms-errors';

@Component({
  selector: 'app-mes-informations-form',
  standalone: true,
  imports: [
    PButtonComponent,
    CommonModule,
    IconsModule,
    PInputComponent,
    ReactiveFormsModule,
    PSelectComponent,
  ],
  templateUrl: './mes-informations-form.component.html',
  styleUrl: './mes-informations-form.component.scss',
})
export class MesInformationsFormComponent implements OnInit, OnChanges {
  private readonly DEBOUNCETIME_TO_SHARE_FORM_IN_MS = 150;

  readonly loginInfo = `• Utilisez uniquement des lettres minuscules et des chiffres.
• Seuls les caractères suivants sont autorisés : point (.), tiret (-), underscore (_).
• Aucun espace n'est autorisé.
• La saisie doit contenir au maximum 100 caractères.`;

  @Input() label!: string;
  @Input() initialValue!: SignUpInformationsData;
  @Input() loginAlreadyExists = false;

  @Output() informationsChanged = new EventEmitter<SignUpInformationsData>();
  @Output() disableNextButton = new EventEmitter<boolean>();
  @Output() verifyLogin = new EventEmitter<string>();
  @Output() cleanLoginError = new EventEmitter();

  form = new FormGroup({
    firstname: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        PervalValidators.firstname,
      ],
    }),
    lastname: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        PervalValidators.lastname,
      ],
    }),
    email: new FormControl('', {
      validators: [
        Validators.required,
        Validators.maxLength(255),
        PervalValidators.email,
      ],
    }),
    login: new FormControl('', {
      validators: [
        Validators.required,
        Validators.maxLength(100),
        pervalLoginValidator(),
      ],
    }),
  });

  ngOnInit(): void {
    this.fillForm();
    this.emitInformationsChanges();
    this.emitNextButtonState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loginAlreadyExists']) {
      this.emitNextButtonState();
    }
  }

  public onVerifyLogin(): void {
    const login = this.form.controls.login.value;

    if (this.form.controls.login.valid && login) {
      this.verifyLogin.emit(login);
    }
  }

  public onLoginInputChange(): void {
    if (this.loginAlreadyExists) {
      this.cleanLoginError.emit();
    }
  }

  private fillForm(): void {
    if (!this.initialValue) {
      return;
    }

    this.form.patchValue(this.initialValue, { emitEvent: false });
  }

  private emitInformationsChanges(): void {
    this.form.valueChanges
      .pipe(debounceTime(this.DEBOUNCETIME_TO_SHARE_FORM_IN_MS))
      .subscribe(() => {
        this.informationsChanged.emit(this.form.getRawValue());
        this.emitNextButtonState();
      });
  }

  private emitNextButtonState(): void {
    this.disableNextButton.emit(!this.form.valid || this.loginAlreadyExists);
  }

  public getErrorMessage(
    controlName: keyof typeof MES_INFORMATIONS_ERROR_MESSAGES,
  ): string {
    return getFirstControlErrorMessage(
      this.form.controls[controlName],
      MES_INFORMATIONS_ERROR_MESSAGES[controlName],
    );
  }
}
